import { ScaleCanvas } from "./ScaleCanvas.js";
import {
  frequencyToFraction,
  formatFrequency,
} from "../../spectrogram/frequencyMapping.js";

/**
 * Frequency ruler for a spectrogram lane.
 *
 * Ticks are placed with the *same* mapping the tiles were rendered with —
 * see spectrogram/frequencyMapping.js — because the axis is mel by default and
 * a linearly-spaced ruler would put 4 kHz at a third of the height where the
 * picture has it at two thirds.
 *
 * ── The bottom is 20 Hz, not 0 ──
 *
 * The server's analysis floor is `minFrequency`, 20 Hz in practice, and this
 * ruler reads it from the manifest rather than assuming zero. Labelling the
 * bottom row "0 Hz" would be a straightforward lie about what was analysed, and
 * on speech there is nothing down there to show anyway.
 */
export class FrequencyScale extends ScaleCanvas {
  /** Steps unlabelled ticks may fall on, finest first — 1-2-5 per decade. */
  static TICK_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

  /**
   * Which leading digit wins among one-figure labels, and which last digit
   * among longer ones. 1-2-5 for the first so the whole axis reads as it always
   * has; halves, then evens, for the rest — 1.5 kHz before 1.2 kHz.
   */
  static FIRST_FIGURE_ORDER = [1, 2, 5, 3, 4, 6, 7, 8, 9];
  static LAST_FIGURE_ORDER = [5, 2, 4, 6, 8, 1, 3, 7, 9];

  /** Labels never need more than this many significant figures. */
  static MAX_FIGURES = 4;

  constructor(canvas, options = {}) {
    super(canvas, options);
    this.range = options.range || null;
    this.scale = options.scale || "mel";
    this.highlights = [];
  }

  /**
   * @param {{min: number, max: number}} range - the visible band
   * @param {string} scale - "mel", "log" or "linear", from the manifest
   */
  setRange(range, scale) {
    this.range = range;
    if (scale) this.scale = scale;
    this.draw();
  }

  /**
   * Bands to tint behind the ticks. Takes effect on the next draw — the
   * renderer sets these and then the range, which draws once for both.
   *
   * @param {Array<{min: number, max: number, color: string}>} highlights
   */
  setHighlights(highlights) {
    this.highlights = highlights || [];
  }

  /**
   * Round values strictly inside the band, roundest first.
   *
   * "Round" is counted in significant figures, not in Hz: 20 Hz and 20 kHz are
   * equally round, which is what suits an axis that is roughly logarithmic.
   * Only the roundest level that has anything inside the band is offered:
   * one-figure values (100, 1k, 10k, then 20, 200, … then 50, …) for any band
   * spanning a power of ten's multiple, two-figure ones (1.1k, 1.2k, …) for a
   * band like 1000–1500, three for 1000–1050. A narrow band reaches for finer
   * labels only when nothing rounder is inside it.
   *
   * @returns {number[]} Hz, in priority order
   */
  static labelCandidates(min, max) {
    const out = [];
    const seen = new Set();
    const firstDecade = Math.floor(Math.log10(Math.max(min, 1)));
    const lastDecade = Math.floor(Math.log10(Math.max(max, 1)));

    for (let figures = 1; figures <= FrequencyScale.MAX_FIGURES; figures++) {
      const level = [];
      for (let decade = firstDecade; decade <= lastDecade; decade++) {
        const step = 10 ** (decade - figures + 1);
        if (step < 1) continue; // sub-Hz labels say nothing a ruler can show
        const from = Math.max(min, 10 ** decade);
        const to = Math.min(max, 10 ** (decade + 1));
        for (let n = Math.ceil(from / step); n * step < to; n++) {
          const hz = n * step;
          if (hz <= min || seen.has(hz)) continue;
          seen.add(hz);
          const order = figures === 1
            ? FrequencyScale.FIRST_FIGURE_ORDER
            : FrequencyScale.LAST_FIGURE_ORDER;
          level.push({ hz, rank: order.indexOf(figures === 1 ? n : n % 10) });
        }
      }
      // Stable, so equal ranks stay low-to-high.
      level.sort((a, b) => a.rank - b.rank);
      out.push(...level.map((c) => c.hz));
      // Stop at the first level with anything inside the band. Going on would
      // fill every leftover gap with whatever fits — 3.2 kHz between 2 and 5 —
      // and labels that irregular read as noise. The gaps are what the
      // unlabelled ticks are for.
      if (out.length > 0) break;
    }
    return out;
  }

  /**
   * Every tick on the ruler, labelled and not.
   *
   * One routine for every band — the whole analysed range and a zoomed one
   * alike — so the ruler depends only on what is shown and how tall it is.
   * Zooming to exactly the default band draws exactly the default ruler.
   *
   * - **Labels**: both ends of the band, always and exactly, then the roundest
   *   values inside it (see labelCandidates) wherever they clear `labelGap`.
   * - **Unlabelled ticks**: between each pair of neighbouring labels, multiples
   *   of the finest 1-2-5 step that divides both labels and keeps ticks
   *   `tickGap` px apart. Dividing both is what keeps the spacing even right up
   *   to each label. A band end can be any value, so it's exempt — ticks just
   *   stay clear of it.
   *
   * Pure arithmetic over `height`, so it's testable without a canvas.
   *
   * @returns {{labels: Array<{hz, y}>, ticks: Array<{hz, y}>}}
   */
  static layout(min, max, height, scale, { labelGap = 16, tickGap = 6 } = {}) {
    const yFor = (hz) => height * (1 - frequencyToFraction(hz, min, max, scale));

    const labels = ScaleCanvas.cull(
      [min, max, ...FrequencyScale.labelCandidates(min, max)].map((hz) => ({ hz, y: yFor(hz) })),
      labelGap
    );

    const isEnd = (hz) => hz === min || hz === max;
    const divides = (step, hz) => Math.abs(hz / step - Math.round(hz / step)) < 1e-9;

    const sorted = [...labels].sort((a, b) => a.hz - b.hz);
    const ticks = [];
    for (let i = 1; i < sorted.length; i++) {
      const lower = sorted[i - 1];
      const upper = sorted[i];

      for (const step of FrequencyScale.TICK_STEPS) {
        if (step >= upper.hz - lower.hz) break;
        const lowerFits = divides(step, lower.hz);
        const upperFits = divides(step, upper.hz);
        if ((!lowerFits && !isEnd(lower.hz)) || (!upperFits && !isEnd(upper.hz))) continue;

        const inside = [];
        for (let n = Math.floor(lower.hz / step) + 1; n * step < upper.hz; n++) {
          inside.push({ hz: n * step, y: yFor(n * step) });
        }
        // Clear of a band end the step doesn't divide; evenly spaced from the rest.
        const kept = inside.filter(
          (t) =>
            (lowerFits || Math.abs(t.y - lower.y) >= tickGap / 2) &&
            (upperFits || Math.abs(t.y - upper.y) >= tickGap / 2)
        );
        const ys = [
          ...(lowerFits ? [lower.y] : []),
          ...kept.map((t) => t.y),
          ...(upperFits ? [upper.y] : []),
        ];
        if (ys.every((y, j) => j === 0 || Math.abs(y - ys[j - 1]) >= tickGap)) {
          ticks.push(...kept);
          break;
        }
      }
    }

    return { labels, ticks };
  }

  render(width, height) {
    if (!this.range) return;

    const theme = this.theme;
    const { min, max } = this.range;
    if (!(max > min)) return;

    const font = theme.font || "10px sans-serif";

    this.ctx.font = font;
    this.ctx.textBaseline = "middle";

    this.#drawHighlights(width, height);

    const fontSize = this.#fontSize(font);
    const { labels, ticks } = FrequencyScale.layout(min, max, height, this.scale, {
      labelGap: (theme.minimumLabelGap ?? 1.6) * fontSize,
      tickGap: theme.minimumTickGap ?? 6,
    });
    this.#drawTicks(width, height, labels, ticks, fontSize);
  }

  /** Full-width tints, under the ticks so they never hide a mark. */
  #drawHighlights(width, height) {
    const { min, max } = this.range;
    for (const band of this.highlights) {
      const top = height * (1 - frequencyToFraction(band.max, min, max, this.scale));
      const bottom = height * (1 - frequencyToFraction(band.min, min, max, this.scale));
      const y0 = Math.max(0, top);
      const y1 = Math.min(height, bottom);
      if (y1 <= y0) continue;
      this.ctx.fillStyle = band.color;
      this.ctx.fillRect(0, y0, width, y1 - y0);
    }
  }

  /** Labelled ticks long with text, the rest short and bare. */
  #drawTicks(width, height, labelled, bare, fontSize) {
    const theme = this.theme;
    const gap = theme.labelGap ?? 4;
    const majorLength = theme.majorTickLength ?? 8;
    const minorLength = theme.minorTickLength ?? 4;

    for (const tick of bare) {
      const [x0, x1] = this.tickSpan(width, minorLength);
      this.hairline(x0, x1, tick.y, theme.minorTickColor || theme.tickColor || "#aaa");
    }

    for (const tick of labelled) {
      const [x0, x1] = this.tickSpan(width, majorLength);
      this.hairline(x0, x1, tick.y, theme.tickColor || "#888");

      const { x, align } = this.labelAnchor(width, majorLength, gap);
      this.ctx.fillStyle = theme.textColor || "#333";
      this.ctx.textAlign = align;
      this.ctx.fillText(
        // Precise everywhere: a label may be 1.25 kHz, and the ends are exact.
        formatFrequency(tick.hz, true),
        x,
        this.#clampBaseline(tick.y, height, fontSize)
      );
    }
  }

  /** The width this scale would like, so a host can size the element. */
  measure() {
    const theme = this.theme;
    this.ctx.font = theme.font || "10px sans-serif";

    // Labels depend on height; a tall ruler shows the most, so measure that.
    const range = this.range || { min: 20, max: 20000 };
    const labels = FrequencyScale.layout(range.min, range.max, 1000, this.scale).labels.map((t) =>
      formatFrequency(t.hz, true)
    );
    const widest = labels.reduce((max, text) => Math.max(max, this.ctx.measureText(text).width), 0);

    return this.widthFor(widest, theme.majorTickLength ?? 8, theme.labelGap ?? 4);
  }

  #fontSize(font) {
    const match = /(\d+(?:\.\d+)?)px/.exec(font);
    return match ? Number(match[1]) : 10;
  }

  #clampBaseline(y, height, fontSize) {
    const half = fontSize / 2;
    return Math.max(half, Math.min(height - half, y));
  }
}
